/**
 * wallet/insights.ts — proactive financial intelligence for the Roamer Wallet™.
 *
 * Pure, deterministic rules over real wallet data. Every insight carries a
 * `basis` naming the evidence it came from; a rule that lacks data emits
 * nothing (never a fabricated claim).
 */

import type { HomeAmount } from './budget';
import { monthStart, sumBetween, cityCosts } from './budget';
import type {
  BudgetSnapshot,
  ExchangeRateEntry,
  SpendingCategory,
  WalletInsight,
} from './models';

const CATEGORY_LABELS: Record<SpendingCategory, string> = {
  housing: 'Housing', food: 'Food', transportation: 'Transportation',
  coworking: 'Coworking', entertainment: 'Entertainment', travel: 'Travel',
  healthcare: 'Healthcare', utilities: 'Utilities', shopping: 'Shopping',
  businessExpenses: 'Business expenses', taxes: 'Taxes', emergency: 'Emergency',
};

export function generateInsights(input: {
  txs: HomeAmount[];
  snapshot: BudgetSnapshot;
  rates: ExchangeRateEntry[];
  homeCurrency: string;
  currentCurrency: string;
  stays: { city: string; arrivedAt: number; departedAt: number | null }[];
  currentCity: string | null;
  now: number;
}): WalletInsight[] {
  const { txs, snapshot, rates, homeCurrency, currentCurrency, stays, currentCity, now } = input;
  const insights: WalletInsight[] = [];
  const mStart = monthStart(now);
  const prevStart = Date.UTC(new Date(mStart).getUTCFullYear(), new Date(mStart).getUTCMonth() - 1, 1);
  const cur = homeCurrency;

  // Budget position.
  if (snapshot.monthlyBudget != null && snapshot.projectedSpending != null) {
    if (snapshot.health === 'healthy') {
      insights.push({
        id: 'budget-under', severity: 'positive',
        message: `Your spending is below budget — projected ${Math.round(snapshot.projectedSpending)} ${cur} against a ${Math.round(snapshot.monthlyBudget)} ${cur} goal.`,
        basis: 'month-to-date expenses vs monthly spending goal',
      });
    } else if (snapshot.health === 'over-budget') {
      insights.push({
        id: 'budget-over', severity: 'warning',
        message: `You've already spent past your ${Math.round(snapshot.monthlyBudget)} ${cur} monthly budget.`,
        basis: 'month-to-date expenses vs monthly spending goal',
      });
    } else if (snapshot.health === 'warning') {
      insights.push({
        id: 'budget-pace', severity: 'warning',
        message: `At the current pace you'll hit ${Math.round(snapshot.projectedSpending)} ${cur}, above your ${Math.round(snapshot.monthlyBudget)} ${cur} budget.`,
        basis: 'straight-line projection of month-to-date expenses',
      });
    }
  }

  // Category share of income (e.g. "Housing is consuming 42% of your income").
  const incomeThisMonth = sumBetween(txs, 'income', mStart, now + 1);
  if (incomeThisMonth > 0) {
    const byCategory = new Map<SpendingCategory, number>();
    for (const t of txs) {
      if (t.kind !== 'expense' || t.category == null || t.at < mStart) continue;
      byCategory.set(t.category, (byCategory.get(t.category) ?? 0) + t.amount);
    }
    for (const [category, amount] of byCategory) {
      const pct = Math.round((amount / incomeThisMonth) * 100);
      if (pct >= 35) {
        insights.push({
          id: `category-share-${category}`, severity: 'warning',
          message: `${CATEGORY_LABELS[category]} is consuming ${pct}% of your income this month.`,
          basis: `this month's ${CATEGORY_LABELS[category].toLowerCase()} expenses vs income`,
        });
      }
    }
  }

  // Exchange-rate movement between the two most recent home↔current entries.
  if (currentCurrency !== homeCurrency) {
    const pair = rates
      .filter((r) => r.from === homeCurrency && r.to === currentCurrency)
      .sort((a, b) => a.at - b.at);
    if (pair.length >= 2) {
      const prev = pair[pair.length - 2].rate;
      const latest = pair[pair.length - 1].rate;
      const deltaPct = ((latest - prev) / prev) * 100;
      if (Math.abs(deltaPct) >= 0.5) {
        insights.push({
          id: 'fx-move',
          severity: deltaPct > 0 ? 'positive' : 'info',
          message: deltaPct > 0
            ? `Exchange rates improved — ${homeCurrency}→${currentCurrency} is up ${deltaPct.toFixed(1)}% since the last reading.`
            : `${homeCurrency}→${currentCurrency} weakened ${Math.abs(deltaPct).toFixed(1)}% since the last reading.`,
          basis: 'two most recent recorded exchange rates',
        });
      }
    }
  }

  // Category increase vs the previous month (e.g. rising food/coffee spend).
  for (const category of Object.keys(CATEGORY_LABELS) as SpendingCategory[]) {
    const thisMonth = txs
      .filter((t) => t.kind === 'expense' && t.category === category && t.at >= mStart)
      .reduce((s, t) => s + t.amount, 0);
    const lastMonth = txs
      .filter((t) => t.kind === 'expense' && t.category === category && t.at >= prevStart && t.at < mStart)
      .reduce((s, t) => s + t.amount, 0);
    if (lastMonth > 0 && thisMonth > lastMonth * 1.25) {
      insights.push({
        id: `category-up-${category}`, severity: 'info',
        message: `${CATEGORY_LABELS[category]} spending increased ${Math.round(((thisMonth - lastMonth) / lastMonth) * 100)}% versus last month.`,
        basis: 'this month vs previous month category totals',
      });
    }
  }

  // Cheaper visited city (never invents cities you haven't lived in).
  if (currentCity) {
    const costs = cityCosts(txs, stays, now).filter((c) => c.perDay != null);
    const here = costs.find((c) => c.city === currentCity);
    const cheapest = costs[0];
    if (here && cheapest && cheapest.city !== currentCity && here.perDay! > cheapest.perDay!) {
      const monthlySaving = Math.round((here.perDay! - cheapest.perDay!) * 30);
      if (monthlySaving > 0) {
        insights.push({
          id: 'cheaper-city', severity: 'info',
          message: `You could save about ${monthlySaving} ${cur}/month at your ${cheapest.city} spending pace.`,
          basis: 'per-day spend across cities you have actually stayed in',
        });
      }
    }
  }

  // Runway.
  if (snapshot.runwayMonths != null) {
    insights.push({
      id: 'runway', severity: snapshot.runwayMonths >= 6 ? 'positive' : 'warning',
      message: `Current runway: ${snapshot.runwayMonths.toFixed(0)} months.`,
      basis: 'lifetime net savings vs average monthly expenses',
    });
  }

  return insights;
}
